// Хранилище неотправленных команд в браузере (015: research, Р5; data-model.md, «Неотправленная
// команда»; FR-017, FR-019; COLLAB-10). IndexedDB `kp-minutes-queue`, ключ — [пользователь,
// протокол, поколение (ревизия перевода), номер по порядку]; значение — команда целиком (номер,
// тело, база), время создания и число попыток. Тонкая обёртка над indexedDB без зависимостей.
// Хранилище недоступно (частный режим, запрет, сбой открытия) — очередь в памяти вкладки и флаг
// durable = false: строка состояния предупреждает, что правки не переживут перезагрузку (ux CHK007).
import type { Command } from '@kp/contracts';
import type { StoredIntent } from './intents.ts';

export const QUEUE_DB = 'kp-minutes-queue';
const STORE = 'commands';
const VERSION = 1;

/** Ключ записи: пользователь, протокол, поколение, номер по порядку создания. */
export type QueueKey = readonly [
  userId: string,
  documentId: string,
  transferRevision: number,
  seq: number,
];

export interface QueueRecord {
  readonly userId: string;
  readonly documentId: string;
  readonly transferRevision: number;
  readonly seq: number;
  /** Logical position; descendants remain before later actions after their parent is acknowledged. */
  readonly orderPath?: readonly number[];
  readonly commandId: string;
  /** Команда целиком: повтор уходит тем же номером и тем же телом. */
  readonly body?: Command;
  /** Action accepted by the UI but waiting behind an earlier command. */
  readonly intent?: StoredIntent;
  readonly baseRevision: number;
  /** Время создания, мс. */
  readonly createdAt: number;
  readonly attempts: number;
}

export function keyOf(record: QueueRecord): QueueKey {
  return [record.userId, record.documentId, record.transferRevision, record.seq];
}

export interface QueueStore {
  /** false — записи живут в памяти вкладки: хранилище браузера недоступно. */
  readonly durable: boolean;
  put(record: QueueRecord, remove?: readonly QueueKey[]): Promise<void>;
  delete(key: QueueKey): Promise<void>;
  /** Записи пользователя (и протокола) по ключу: поколение, затем порядок создания. */
  list(userId: string, documentId?: string): Promise<QueueRecord[]>;
}

function compareKeys(a: QueueKey, b: QueueKey): number {
  for (let index = 0; index < a.length; index += 1) {
    const left = a[index]!;
    const right = b[index]!;
    if (left < right) return -1;
    if (left > right) return 1;
  }
  return 0;
}

/**
 * Очередь в памяти: тесты и запасной путь без IndexedDB. durable — для тестов, которые
 * изображают настоящее хранилище; запасной путь — false.
 */
export function memoryQueueStore(options: { durable?: boolean } = {}): QueueStore {
  const records = new Map<string, QueueRecord>();
  const id = (key: QueueKey) => JSON.stringify(key);
  return {
    durable: options.durable ?? false,
    put(record, remove = []) {
      for (const key of remove) records.delete(id(key));
      records.set(id(keyOf(record)), record);
      return Promise.resolve();
    },
    delete(key) {
      records.delete(id(key));
      return Promise.resolve();
    },
    list(userId, documentId) {
      const found = Array.from(records.values()).filter(
        (record) =>
          record.userId === userId &&
          (documentId === undefined || record.documentId === documentId),
      );
      found.sort((a, b) => compareKeys(keyOf(a), keyOf(b)));
      return Promise.resolve(found);
    },
  };
}

// --- IndexedDB ---

function done<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB'));
  });
}

/** Запись считается сохранённой, когда транзакция завершена (а не когда запрос принят). */
function committed(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => reject(transaction.error ?? new Error('IndexedDB'));
    transaction.onerror = () => reject(transaction.error ?? new Error('IndexedDB'));
  });
}

function openDatabase(factory: IDBFactory): Promise<IDBDatabase> {
  const request = factory.open(QUEUE_DB, VERSION);
  request.onupgradeneeded = () => {
    const db = request.result;
    if (!db.objectStoreNames.contains(STORE)) {
      db.createObjectStore(STORE, {
        keyPath: ['userId', 'documentId', 'transferRevision', 'seq'],
      });
    }
  };
  return done(request);
}

/** Диапазон ключей пользователя или пользователя и протокола: массив больше любой строки. */
function rangeOf(userId: string, documentId?: string): IDBKeyRange {
  return documentId === undefined
    ? IDBKeyRange.bound([userId], [userId, []])
    : IDBKeyRange.bound([userId, documentId], [userId, documentId, []]);
}

/**
 * Хранилище на IndexedDB с запасным путём: открытие или запись не удались — дальше память,
 * durable = false. Записи, что успели лечь в IndexedDB, остаются в нём до следующего открытия.
 */
class BrowserQueueStore implements QueueStore {
  private db: Promise<IDBDatabase | null>;
  private fallback: QueueStore | null = null;

  constructor(factory: IDBFactory | null) {
    if (!factory) this.degrade();
    this.db = factory
      ? openDatabase(factory).catch(() => {
          this.degrade();
          return null;
        })
      : Promise.resolve(null);
  }

  get durable(): boolean {
    return this.fallback === null;
  }

  private degrade(): QueueStore {
    this.fallback ??= memoryQueueStore();
    return this.fallback;
  }

  private async run<T>(
    work: (db: IDBDatabase) => Promise<T>,
    spare: (store: QueueStore) => Promise<T>,
  ): Promise<T> {
    const db = await this.db;
    if (db && !this.fallback) {
      try {
        return await work(db);
      } catch {
        // Запись не легла (переполнено, сбой): дальше — память вкладки и предупреждение.
        return spare(this.degrade());
      }
    }
    return spare(this.degrade());
  }

  put(record: QueueRecord, remove: readonly QueueKey[] = []): Promise<void> {
    return this.run(
      async (db) => {
        const transaction = db.transaction(STORE, 'readwrite');
        const store = transaction.objectStore(STORE);
        store.put(record);
        for (const key of remove) store.delete([...key]);
        await committed(transaction);
      },
      (store) => store.put(record, remove),
    );
  }

  delete(key: QueueKey): Promise<void> {
    return this.run(
      async (db) => {
        const transaction = db.transaction(STORE, 'readwrite');
        transaction.objectStore(STORE).delete([...key]);
        await committed(transaction);
      },
      (store) => store.delete(key),
    );
  }

  list(userId: string, documentId?: string): Promise<QueueRecord[]> {
    return this.run(
      async (db) => {
        const transaction = db.transaction(STORE, 'readonly');
        const found = await done(
          transaction.objectStore(STORE).getAll(rangeOf(userId, documentId)),
        );
        return (found as QueueRecord[]).filter(
          (record) =>
            record.userId === userId &&
            (documentId === undefined || record.documentId === documentId),
        );
      },
      (store) => store.list(userId, documentId),
    );
  }
}

function browserFactory(): IDBFactory | null {
  try {
    return typeof indexedDB === 'undefined' ? null : indexedDB;
  } catch {
    return null;
  }
}

let shared: QueueStore | null = null;

/** Одно хранилище на вкладку: одно соединение с IndexedDB для всех протоколов. */
export function queueStore(): QueueStore {
  shared ??= new BrowserQueueStore(browserFactory());
  return shared;
}

/** Тесты экрана: подменить хранилище вкладки (null — снова браузерное при следующем вызове). */
export function setQueueStore(store: QueueStore | null): void {
  shared = store;
}
